/**
 * Inventory Hooks - React hooks for inventory data management
 */

import {
  createWarehouseTransfer,
  getCentralRequests,
  getInventory,
  getInventoryById,
  getInventoryGroups,
  getInventoryMovements,
  getLowStockAlerts,
  getPhantomCount,
  getStockCounts,
  getWarehouses,
  lookupBarcode,
  type BarcodeLookupResult,
  type CentralRequest,
  type CreateWarehouseTransferData,
  type InventoryFilters,
  type InventoryGroupInfo,
  type InventoryItem,
  type InventoryListResponse,
  type LowStockAlert,
  type MovementFilters,
  type StockCount,
  type StockCountFilters,
  type StockMovementRecord,
  type Warehouse,
  type WarehouseTransfer,
} from '@/api/inventory';
import { useCallback, useEffect, useState } from 'react';

// ============================================
// TYPES
// ============================================

interface UseInventoryResult {
  items: InventoryItem[];
  total: number;
  isLoading: boolean;
  error: string | null;
  refetch: () => Promise<void>;
}

interface UseInventoryItemResult {
  item: InventoryItem | null;
  isLoading: boolean;
  error: string | null;
  refetch: () => Promise<void>;
}

interface UseLowStockAlertsResult {
  alerts: LowStockAlert[];
  isLoading: boolean;
  error: string | null;
  refetch: () => Promise<void>;
}

interface UseWarehousesResult {
  warehouses: Warehouse[];
  isLoading: boolean;
  error: string | null;
  refetch: () => Promise<void>;
}

// ============================================
// HOOKS
// ============================================

/**
 * Hook to fetch and manage inventory list
 */
export function useInventory(filters: InventoryFilters = {}): UseInventoryResult {
  const [items, setItems] = useState<InventoryItem[]>([]);
  const [total, setTotal] = useState(0);
  const [isLoading, setIsLoading] = useState(true);
  const [error, setError] = useState<string | null>(null);

  const fetchInventory = useCallback(async () => {
    setIsLoading(true);
    setError(null);
    try {
      const response: InventoryListResponse = await getInventory(filters);
      setItems(response.items);
      setTotal(response.total);
    } catch (err) {
      const message = err instanceof Error ? err.message : 'Failed to fetch inventory';
      setError(message);
      console.error('Error fetching inventory:', err);
    } finally {
      setIsLoading(false);
    }
  }, [
    filters.warehouseId,
    filters.productId,
    filters.type,
    filters.status,
    filters.search,
    filters.inventoryGroup,
    filters.offset,
    filters.limit,
  ]);

  useEffect(() => {
    void fetchInventory();
  }, [fetchInventory]);

  return {
    items,
    total,
    isLoading,
    error,
    refetch: fetchInventory,
  };
}

/**
 * Hook to fetch single inventory item
 */
export function useInventoryItem(id: string | undefined): UseInventoryItemResult {
  const [item, setItem] = useState<InventoryItem | null>(null);
  const [isLoading, setIsLoading] = useState(true);
  const [error, setError] = useState<string | null>(null);

  const fetchItem = useCallback(async () => {
    if (!id) {
      setItem(null);
      setIsLoading(false);
      return;
    }

    setIsLoading(true);
    setError(null);
    try {
      const response = await getInventoryById(id);
      setItem(response.data);
    } catch (err) {
      const message = err instanceof Error ? err.message : 'Failed to fetch inventory item';
      setError(message);
      console.error('Error fetching inventory item:', err);
    } finally {
      setIsLoading(false);
    }
  }, [id]);

  useEffect(() => {
    void fetchItem();
  }, [fetchItem]);

  return {
    item,
    isLoading,
    error,
    refetch: fetchItem,
  };
}

/**
 * Hook to fetch low stock alerts
 */
export function useLowStockAlerts(warehouseId?: string): UseLowStockAlertsResult {
  const [alerts, setAlerts] = useState<LowStockAlert[]>([]);
  const [isLoading, setIsLoading] = useState(true);
  const [error, setError] = useState<string | null>(null);

  const fetchAlerts = useCallback(async () => {
    setIsLoading(true);
    setError(null);
    try {
      const response = await getLowStockAlerts(warehouseId);
      setAlerts(response.data);
    } catch (err) {
      const message = err instanceof Error ? err.message : 'Failed to fetch low stock alerts';
      setError(message);
      console.error('Error fetching low stock alerts:', err);
    } finally {
      setIsLoading(false);
    }
  }, [warehouseId]);

  useEffect(() => {
    void fetchAlerts();
  }, [fetchAlerts]);

  return {
    alerts,
    isLoading,
    error,
    refetch: fetchAlerts,
  };
}

/**
 * Hook to fetch warehouses
 */
export function useWarehouses(): UseWarehousesResult {
  const [warehouses, setWarehouses] = useState<Warehouse[]>([]);
  const [isLoading, setIsLoading] = useState(true);
  const [error, setError] = useState<string | null>(null);

  const fetchWarehouses = useCallback(async () => {
    setIsLoading(true);
    setError(null);
    try {
      const response = await getWarehouses();
      // M-022 fix: Runtime type guard instead of unsafe cast
      const warehouseData = response.data;
      if (Array.isArray(warehouseData)) {
        setWarehouses(warehouseData as Warehouse[]);
      } else if (
        warehouseData &&
        typeof warehouseData === 'object' &&
        'warehouses' in warehouseData
      ) {
        setWarehouses((warehouseData as { warehouses: Warehouse[] }).warehouses);
      } else {
        console.warn('[useWarehouses] Unexpected API response shape:', warehouseData);
        setWarehouses([]);
      }
    } catch (err) {
      const message = err instanceof Error ? err.message : 'Failed to fetch warehouses';
      setError(message);
      console.error('Error fetching warehouses:', err);
    } finally {
      setIsLoading(false);
    }
  }, []);

  useEffect(() => {
    void fetchWarehouses();
  }, [fetchWarehouses]);

  return {
    warehouses,
    isLoading,
    error,
    refetch: fetchWarehouses,
  };
}

interface UseInventoryMovementsResult {
  movements: StockMovementRecord[];
  isLoading: boolean;
  error: string | null;
  refetch: () => Promise<void>;
}

/**
 * Hook to fetch inventory movement history
 */
export function useInventoryMovements(filters: MovementFilters = {}): UseInventoryMovementsResult {
  const [movements, setMovements] = useState<StockMovementRecord[]>([]);
  const [isLoading, setIsLoading] = useState(true);
  const [error, setError] = useState<string | null>(null);

  const fetchMovements = useCallback(async () => {
    setIsLoading(true);
    setError(null);
    try {
      // Don't pass type to backend - enum values differ; filter client-side after normalization
      const response = await getInventoryMovements({
        search: filters.search,
        page: filters.page,
        pageSize: filters.pageSize,
      });
      const data = filters.type
        ? response.data.filter(m => m.type === filters.type)
        : response.data;
      setMovements(data);
    } catch (err) {
      const message = err instanceof Error ? err.message : 'Failed to fetch inventory movements';
      setError(message);
      console.error('Error fetching inventory movements:', err);
    } finally {
      setIsLoading(false);
    }
  }, [filters.search, filters.type, filters.page, filters.pageSize]);

  useEffect(() => {
    void fetchMovements();
  }, [fetchMovements]);

  return {
    movements,
    isLoading,
    error,
    refetch: fetchMovements,
  };
}

// ============================================
// STOCK COUNT HOOK (Epic 24, Q-T109 fázis-1: route + lista)
// ============================================

interface UseStockCountsResult {
  stockCounts: StockCount[];
  isLoading: boolean;
  error: string | null;
  refetch: () => Promise<void>;
}

/**
 * Hook to fetch the stock count (leltár) list.
 * BE: GET /api/v1/stock-counts (StockCountController#listStockCounts).
 */
export function useStockCounts(filters: StockCountFilters = {}): UseStockCountsResult {
  const [stockCounts, setStockCounts] = useState<StockCount[]>([]);
  const [isLoading, setIsLoading] = useState(true);
  const [error, setError] = useState<string | null>(null);

  const fetchStockCounts = useCallback(async () => {
    setIsLoading(true);
    setError(null);
    try {
      const response = await getStockCounts(filters);
      setStockCounts(response.data);
    } catch (err) {
      const message = err instanceof Error ? err.message : 'Failed to fetch stock counts';
      setError(message);
      console.error('Error fetching stock counts:', err);
    } finally {
      setIsLoading(false);
    }
  }, [
    filters.status,
    filters.warehouseId,
    filters.locationId,
    filters.dateFrom,
    filters.dateTo,
    filters.offset,
    filters.limit,
  ]);

  useEffect(() => {
    void fetchStockCounts();
  }, [fetchStockCounts]);

  return {
    stockCounts,
    isLoading,
    error,
    refetch: fetchStockCounts,
  };
}

// ============================================
// WAREHOUSE TRANSFER HOOK (multi-warehouse Phase-1b)
// ============================================

interface UseCreateWarehouseTransferResult {
  transfer: (data: CreateWarehouseTransferData) => Promise<WarehouseTransfer>;
  isLoading: boolean;
  error: string | null;
}

/**
 * Hook to create a warehouse-to-warehouse transfer (házon-belüli átcsoportosítás).
 * Készlet-write — a BE POST /warehouses/transfers atomi forrás→cél mozgás.
 */
export function useCreateWarehouseTransfer(): UseCreateWarehouseTransferResult {
  const [isLoading, setIsLoading] = useState(false);
  const [error, setError] = useState<string | null>(null);

  const transfer = useCallback(
    async (data: CreateWarehouseTransferData): Promise<WarehouseTransfer> => {
      setIsLoading(true);
      setError(null);
      try {
        const response = await createWarehouseTransfer(data);
        return response.data;
      } catch (err) {
        const message = err instanceof Error ? err.message : 'Nem sikerült az átcsoportosítás';
        setError(message);
        throw err;
      } finally {
        setIsLoading(false);
      }
    },
    []
  );

  return { transfer, isLoading, error };
}

// ============================================
// INVENTORY GROUPS HOOK (O-INV-2)
// ============================================

interface UseInventoryGroupsResult {
  groups: InventoryGroupInfo[];
  isLoading: boolean;
  error: string | null;
}

/**
 * Hook to fetch inventory groups with RBAC visibility
 */
export function useInventoryGroups(): UseInventoryGroupsResult {
  const [groups, setGroups] = useState<InventoryGroupInfo[]>([]);
  const [isLoading, setIsLoading] = useState(true);
  const [error, setError] = useState<string | null>(null);

  const fetchGroups = useCallback(async () => {
    setIsLoading(true);
    setError(null);
    try {
      const response = await getInventoryGroups();
      setGroups(response.data);
    } catch (err) {
      const message = err instanceof Error ? err.message : 'Failed to fetch inventory groups';
      setError(message);
    } finally {
      setIsLoading(false);
    }
  }, []);

  useEffect(() => {
    void fetchGroups();
  }, [fetchGroups]);

  return { groups, isLoading, error };
}

// ============================================
// INV-FG-7-1: BARCODE LOOKUP HOOK
// ============================================

interface UseBarcodeLookupResult {
  result: BarcodeLookupResult | null;
  isLoading: boolean;
  error: string | null;
  lookup: (barcode: string) => Promise<BarcodeLookupResult | null>;
  clear: () => void;
}

/**
 * Hook for barcode scanning & lookup
 */
export function useBarcodeLookup(): UseBarcodeLookupResult {
  const [result, setResult] = useState<BarcodeLookupResult | null>(null);
  const [isLoading, setIsLoading] = useState(false);
  const [error, setError] = useState<string | null>(null);

  const lookup = useCallback(async (barcode: string): Promise<BarcodeLookupResult | null> => {
    if (!barcode.trim()) return null;
    setIsLoading(true);
    setError(null);
    try {
      const response = await lookupBarcode(barcode.trim());
      setResult(response.data);
      return response.data;
    } catch (err) {
      const message = err instanceof Error ? err.message : 'Barcode lookup failed';
      setError(message);
      setResult(null);
      return null;
    } finally {
      setIsLoading(false);
    }
  }, []);

  const clear = useCallback(() => {
    setResult(null);
    setError(null);
  }, []);

  return { result, isLoading, error, lookup, clear };
}

// ============================================
// INV-FG-7-3: CENTRAL REQUESTS HOOK
// ============================================

interface UseCentralRequestsResult {
  requests: CentralRequest[];
  isLoading: boolean;
  error: string | null;
  refetch: () => Promise<void>;
}

/**
 * Hook to fetch pending central warehouse requests
 */
export function useCentralRequests(): UseCentralRequestsResult {
  const [requests, setRequests] = useState<CentralRequest[]>([]);
  const [isLoading, setIsLoading] = useState(true);
  const [error, setError] = useState<string | null>(null);

  const fetchRequests = useCallback(async () => {
    setIsLoading(true);
    setError(null);
    try {
      const response = await getCentralRequests();
      setRequests(response.data);
    } catch (err) {
      const message = err instanceof Error ? err.message : 'Failed to fetch central requests';
      setError(message);
    } finally {
      setIsLoading(false);
    }
  }, []);

  useEffect(() => {
    void fetchRequests();
  }, [fetchRequests]);

  return { requests, isLoading, error, refetch: fetchRequests };
}

// ============================================
// INV-FG-7-4: PHANTOM COUNT HOOK
// ============================================

interface UsePhantomCountResult {
  count: number;
  isLoading: boolean;
  error: string | null;
  refetch: () => Promise<void>;
}

/**
 * Hook to fetch phantom inventory item count
 */
export function usePhantomCount(): UsePhantomCountResult {
  const [count, setCount] = useState(0);
  const [isLoading, setIsLoading] = useState(true);
  const [error, setError] = useState<string | null>(null);

  const fetchCount = useCallback(async () => {
    setIsLoading(true);
    setError(null);
    try {
      const response = await getPhantomCount();
      setCount(response.data.count);
    } catch (err) {
      const message = err instanceof Error ? err.message : 'Failed to fetch phantom count';
      setError(message);
    } finally {
      setIsLoading(false);
    }
  }, []);

  useEffect(() => {
    void fetchCount();
  }, [fetchCount]);

  return { count, isLoading, error, refetch: fetchCount };
}
